/**
 * **可复制的房间号（就是那行字，不是按钮）。**
 *
 * 界面层不许自己挂指针事件（`test/ui/design.test.ts`），所以 hover / 按下 /
 * 复制反馈收在这个组件里，走和别的控件同一套 `createSurface` 状态机 ——
 * 但**不画底板**，只改文字亮度：
 *
 * - hover：提亮
 * - 按下：压暗
 * - 复制成功：换成另一种颜色，**两秒后**变回房间号
 */
import { Container, type Text } from 'pixi.js';
import { createSurface, type ControlState } from './state';
import { INK } from './style';
import { richLabel } from './richText';
import type { Widget } from './base';

/** 复制成功后改变颜色多久（毫秒） */
const COPIED_MS = 2000;

export interface CopyCodeSpec {
  readonly scale: number;
  readonly text: string;
  readonly size?: number;
  /**
   * 实际复制到剪贴板的内容。不给就复制 text。
   *
   * ⚠️ 房间号那行**显示**的是 #ABCD，**复制**的却是一整条邀请链接 ——
   * 两者本来就该分开。
   */
  readonly copyText?: string;
}

export function copyCode(spec: CopyCodeSpec): Widget {
  const text: Text = richLabel({
    text: spec.text,
    scale: spec.scale,
    size: spec.size ?? 40,
    fill: INK.accent,
    role: 'display',
    tracking: 6,
  });
  const container = new Container();
  container.addChild(text);

  const width = Math.ceil(text.width);
  const height = Math.ceil(text.height);

  let copied = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const surface = createSurface({
    container,
    width,
    height,
    cursor: 'pointer',
    onPress: () => {
      void copy(spec.copyText ?? spec.text);
      copied = true;
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(() => {
        copied = false;
        paint(surface.state);
      }, COPIED_MS);
      paint(surface.state);
    },
    onStateChange: (state) => paint(state),
    onFrame: (state) => paint(state),
  });

  function paint(state: ControlState): void {
    if (copied) {
      text.style.fill = INK.text;
      return;
    }
    if (state === 'press') text.style.fill = toward(INK.accent, 0x000000, 0.35);
    else if (state === 'hover') text.style.fill = toward(INK.accent, 0xffffff, 0.45);
    else text.style.fill = INK.accent;
  }

  paint(surface.state);

  return { container, width, height };
}

/** 把 `color` 朝 `target` 混 `t`（0..1） */
function toward(color: number, target: number, t: number): number {
  const ch = (shift: number): number => {
    const a = (color >> shift) & 0xff;
    const b = (target >> shift) & 0xff;
    return Math.round(a + (b - a) * t) & 0xff;
  };
  return (ch(16) << 16) | (ch(8) << 8) | ch(0);
}

async function copy(value: string): Promise<void> {
  try {
    await navigator.clipboard?.writeText(value);
  } catch {
    // 剪贴板不可用（没有权限 / 非安全上下文）就当没发生，别把界面弄崩
  }
}
